/**
 * IMPORTS
 */
import { FileText, Paperclip } from "lucide-react";

import {
  ehImagemAnexo,
  type IAnexoMensagem,
  type IMensagemConversa,
} from "@/interface/conversa-cliente";
import { formatarDataHora } from "@/utils/formatar-tempo-relativo";

import { formatarTamanhoArquivo, obterIniciais } from "../helpers/formatadores-conversa";
import { AnexoImagemPreview } from "./anexo-imagem-preview";

type MensagemConversaProps = {
  mensagem: IMensagemConversa;
  onBaixarAnexo: (anexo: IAnexoMensagem) => void;
  carregarImagem: (anexo: IAnexoMensagem) => Promise<Blob>;
};

function MensagemConversa({ mensagem, onBaixarAnexo, carregarImagem }: MensagemConversaProps) {
  const ehMinha = mensagem.eh_minha;
  const imagens = mensagem.anexos.filter((anexo) =>
    ehImagemAnexo({ mimeType: anexo.mime_type, nome: anexo.nome_original })
  );
  const arquivos = mensagem.anexos.filter((anexo) => !imagens.includes(anexo));

  return (
    <article className={`flex gap-3 ${ehMinha ? "flex-row-reverse" : ""}`}>
      <span
        className={`flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
          mensagem.autor.tipo === "interno"
            ? "bg-orange-100 text-orange-700"
            : "bg-gray-200 text-gray-700"
        }`}
        aria-hidden
      >
        {obterIniciais(mensagem.autor.nome)}
      </span>

      <div
        className={`flex max-w-[85%] flex-col gap-1 md:max-w-[70%] ${ehMinha ? "items-end" : ""}`}
      >
        <header className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span className="font-medium text-gray-900">{mensagem.autor.nome}</span>
          {mensagem.autor.tipo === "interno" && (
            <span className="rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
              Equipe Letscom
            </span>
          )}
          <time dateTime={mensagem.created_at}>{formatarDataHora(mensagem.created_at)}</time>
          {mensagem.editada && <span>(editada)</span>}
        </header>

        <div
          className={`rounded-2xl border px-4 py-3 text-sm shadow-sm ${
            ehMinha
              ? "rounded-tr-sm border-orange-200 bg-orange-50"
              : "rounded-tl-sm border-gray-200 bg-white"
          }`}
        >
          {mensagem.conteudo && (
            <p className="whitespace-pre-wrap break-words text-gray-900">{mensagem.conteudo}</p>
          )}

          {imagens.length > 0 && (
            <ul
              className={`mt-3 flex flex-wrap gap-2 ${ehMinha ? "justify-end" : ""}`}
              aria-label="Imagens"
            >
              {imagens.map((anexo) => (
                <li key={anexo.id}>
                  <AnexoImagemPreview
                    anexo={anexo}
                    carregar={() => carregarImagem(anexo)}
                    onBaixar={() => onBaixarAnexo(anexo)}
                  />
                </li>
              ))}
            </ul>
          )}

          {arquivos.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5" aria-label="Anexos">
              {arquivos.map((anexo) => (
                <li key={anexo.id}>
                  <button
                    type="button"
                    onClick={() => onBaixarAnexo(anexo)}
                    title={`Baixar ${anexo.nome_original}`}
                    className="flex w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-left text-xs hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-custom900"
                  >
                    {anexo.mime_type === "application/pdf" ? (
                      <FileText className="size-4 shrink-0" />
                    ) : (
                      <Paperclip className="size-4 shrink-0" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{anexo.nome_original}</span>
                    <span className="shrink-0 text-gray-500">
                      {formatarTamanhoArquivo(anexo.tamanho_bytes)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </article>
  );
}

/**
 * EXPORTS
 */
export { MensagemConversa };
